import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import axios from "axios";
import { FaCrown, FaGem, FaTimes } from "react-icons/fa";
import { motion } from "framer-motion";
import useTranslation from "../hooks/useTranslation";
import LanguageSelector from "../components/LanguageSelector";

const Login = () => {
  const { t } = useTranslation();
  const { login, user } = useUser();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("monthly");
  const [isProcessing, setIsProcessing] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const savedUsername = localStorage.getItem("rememberedUsername");
    if (savedUsername) {
      setUsername(savedUsername);
      setRememberMe(true);
    }

    const token = localStorage.getItem("token");
    if (token && !user) {
      login(token);
    }
  }, [login, user]);

  const handleRememberMeChange = (e) => {
    const isChecked = e.target.checked;
    setRememberMe(isChecked);
    if (!isChecked) localStorage.removeItem("rememberedUsername");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(false);

    try {
      console.log("Sending login request for user:", username);

      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/login`, {
        username,
        password,
      });

      console.log("Login response received:", response.data);

      if (rememberMe) {
        localStorage.setItem("rememberedUsername", username);
      } else {
        localStorage.removeItem("rememberedUsername");
      }

      // PRIMA DATĂ apelează login() pentru a seta utilizatorul în context
      await login(response.data.token, response.data.language);

      // Acum verifică statusul subscriptiei
      try {
        const subResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/subscriptions/status`,
          {
            headers: { Authorization: `Bearer ${response.data.token}` },
          },
        );

        console.log("Subscription status:", subResponse.data);

        const isSubscriptionActive =
          subResponse.data.status === "active" &&
          new Date(subResponse.data.end_date) > new Date();
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        sessionStorage.setItem("matchesSelectedDate", today.toISOString());

        if (!isSubscriptionActive) {
          setSubscriptionStatus(subResponse.data);
          setShowSubscriptionModal(true);
          // Utilizatorul e deja logat, dar vede modalul pentru upgrade
        } else {
          // Navighează doar dacă subscriptia e activă
          navigate("/");
        }
      } catch (subError) {
        console.error("Error checking subscription:", subError);
        // Dacă nu poți verifica subscriptia, loghează utilizatorul oricum
        navigate("/");
      }
    } catch (err) {
      console.error("Login error:", err);
      console.error("Error response:", err.response?.data);
      setError(true);
    }
  };

  const handleSubscribe = async () => {
    setIsProcessing(true);
    setSubscriptionError("");
    try {
      const token = localStorage.getItem("token");

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/subscriptions/upgrade`,
        { plan: selectedPlan },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // După upgrade, obține datele actualizate ale utilizatorului
      const userResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/user`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Reîncarcă utilizatorul în context cu datele actualizate
      await login(token, userResponse.data.user?.language || "en");

      // Închide modalul și navighează
      setShowSubscriptionModal(false);
      navigate("/");
    } catch (err) {
      console.error("Subscription upgrade error:", err);
      setSubscriptionError(
        err.response?.data?.message || t("subscriptionFailed"),
      );
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const subscriptionPlans = [
    {
      name: t("monthlyPro"),
      price: "10€",
      features: [
        t("allProFeatures"),
        t("advancedStatsGraphics"),
        t("support24"),
      ],
      type: "monthly",
      icon: <FaGem className="text-green-400 text-xl" />,
    },
    {
      name: t("yearlyElite"),
      price: "100€",
      features: [
        t("allEliteFeatures"),
        t("twoMonthsFree"),
        t("advancedStatsGraphics"),
        t("prioritySupport"),
      ],
      type: "yearly",
      icon: <FaCrown className="text-yellow-400 text-xl" />,
    },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-black via-gray-900 to-black p-4 relative">
      <motion.div
        className="relative bg-gray-900/80 border border-green-500/30 rounded-2xl shadow-lg shadow-green-500/30 p-8 max-w-md w-full backdrop-blur-xl"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="absolute inset-0 rounded-2xl border border-green-500/20 shadow-[0_0_40px_rgba(34,197,94,0.4)] pointer-events-none"></div>

        {error && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center bg-black/70 z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="bg-red-600/90 p-4 rounded-lg shadow-lg max-w-sm w-full mx-4">
              <p className="font-bold text-white text-center">
                {t("invalidCredentials")}
              </p>
              <p className="text-gray-200 text-center">
                {t("checkUsernamePassword")}
              </p>
            </div>
          </motion.div>
        )}

        <div className="absolute -top-3 -right-3 z-20">
          <LanguageSelector />
        </div>

        <div className="pt-6 text-center">
          <h1 className="text-3xl font-bold text-green-400 mb-2 text-center tracking-wide">
            {t("welcome")}
          </h1>
          <p className="text-gray-400 mb-6">{t("signIn")}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              {t("username")}
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800/60 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-green-400 focus:border-green-400 transition"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              {t("password")}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800/60 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-green-400 focus:border-green-400 transition"
              required
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center text-sm text-gray-300">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={handleRememberMeChange}
                className="mr-2 text-green-500"
              />
              {t("rememberMe")}
            </label>
            <a
              // href="/forgot-password"
              href="#"
              className="text-sm text-green-400 hover:underline"
            >
              {t("forgotPassword")}
            </a>
          </div>

          <button
            type="submit"
            className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white font-bold py-3 px-4 rounded-lg shadow-[0_0_20px_rgba(34,197,94,0.6)] hover:from-green-600 hover:to-green-700 transition-all duration-200 mt-2"
          >
            {t("login")}
          </button>
        </form>

        <p className="text-gray-400 text-sm mt-6 text-center">
          {t("noAccount")}{" "}
          <a
            // href="/register"
            href="#"
            className="text-green-400 hover:text-green-300 underline"
          >
            {t("createAccount")}
          </a>
        </p>
      </motion.div>

      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <motion.div
            className="bg-gray-900/90 border border-green-500/30 rounded-2xl shadow-lg shadow-green-500/30 max-w-2xl w-full p-6 backdrop-blur-xl"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">
                {t("upgradeAccount")}
              </h2>
              <button
                onClick={() => {
                  // Închide modalul dar lasă utilizatorul logat
                  setShowSubscriptionModal(false);
                  navigate("/");
                }}
                className="text-gray-400 hover:text-white"
              >
                <FaTimes size={24} />
              </button>
            </div>

            <div className="mb-6 bg-red-900/40 p-4 rounded-lg border border-red-700/50">
              <h3 className="text-xl font-bold text-white mb-1">
                {t("subscriptionRequired")}
              </h3>
              <p className="text-red-100">{t("freeTrialEnded")}</p>
            </div>

            {subscriptionError && (
              <div className="mb-4 p-3 bg-red-600/90 text-white rounded-lg text-center">
                {subscriptionError}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {subscriptionPlans.map((plan) => (
                <motion.div
                  key={plan.type}
                  onClick={() => setSelectedPlan(plan.type)}
                  className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedPlan === plan.type
                      ? "border-green-400 bg-gray-800/60 shadow-[0_0_20px_rgba(34,197,94,0.4)]"
                      : "border-gray-700 hover:border-gray-600"
                  }`}
                  whileHover={{ scale: 1.03 }}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-bold text-white flex items-center gap-1">
                        {plan.icon}
                        {plan.name}
                      </h3>
                    </div>
                    <div
                      className={`absolute top-4 right-4 ${
                        selectedPlan === plan.type
                          ? "bg-green-900/30 border border-green-400"
                          : "bg-black/20"
                      } rounded-full px-3 py-1`}
                    >
                      <p className="text-xl font-bold text-white">
                        {plan.price}
                      </p>
                    </div>
                  </div>
                  <ul className="mt-6 space-y-1">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="text-gray-300 text-sm">
                        ✓ {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>

            <button
              onClick={handleSubscribe}
              disabled={isProcessing}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white font-bold py-3 px-4 rounded-lg shadow-[0_0_20px_rgba(34,197,94,0.6)] hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center justify-center"
            >
              {isProcessing ? (
                t("processing")
              ) : selectedPlan === "yearly" ? (
                <>
                  <FaCrown className="mr-2" />
                  {t("getTwoMonthsFree")}
                </>
              ) : (
                <>
                  <FaGem className="mr-2" />
                  {t("continueAccess")}
                </>
              )}
            </button>

            <button
              onClick={() => {
                setShowSubscriptionModal(false);
                navigate("/");
              }}
              className="w-full mt-4 bg-gray-800 text-gray-300 font-bold py-3 px-4 rounded-lg hover:bg-gray-700 transition-all duration-200"
            >
              {t("continueWithoutUpgrade")}
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Login;
